import { h, C, MEMBER_COLOR, txt, n2, fmtSigned } from './svgKit';

// Lever (nomograph) diagram: three vertical axes S - C - R, spaced S-C : C-R = Zr : Zs
// (positions from the backend), each member's speed plotted as a point. The Willis equation
// is linear, so the three points always lie on one straight line. Torque arrows on each axis
// show the member torque (up = positive), length proportional to |T|.
//
// Label placement avoids collisions for any tooth counts: the carrier always sits right of
// the middle (Zr > Zs), so the S and C labels run leftwards and the R labels rightwards.

const VW = 360;
const VH = 286;
const X0 = 78;
const X1 = 286;
const Y_TOP = 52;
const Y_BOT = 222;
const MAX_ARROW = 44;

const AXIS_TEXT = {
  ths: { sun: ['Sun', 'MG1'], carrier: ['Carrier', 'Engine'], ring: ['Ring', 'MG2 + Output'] },
};

function niceRange(values) {
  let lo = Math.min(0, ...values);
  let hi = Math.max(0, ...values);
  if (hi - lo < 1) { lo -= 500; hi += 500; }
  const pad = (hi - lo) * 0.12;
  return [lo - pad, hi + pad];
}

const LeverDiagram = ({ lever, gear, generalLabels }) => {
  const xs = Object.fromEntries(lever.points.map((p) => [p.member, X0 + (X1 - X0) * p.x]));
  const rpm = Object.fromEntries(lever.points.map((p) => [p.member, p.rpm]));
  const tq = Object.fromEntries(lever.points.map((p) => [p.member, p.torqueNm]));
  const [lo, hi] = niceRange(Object.values(rpm));
  const y = (v) => Y_BOT - ((v - lo) / (hi - lo)) * (Y_BOT - Y_TOP);
  const maxT = Math.max(1e-9, ...Object.values(tq).map((t) => Math.abs(t)));
  const anchors = { sun: 'middle', carrier: 'end', ring: 'start' };
  const labelX = { sun: xs.sun, carrier: xs.carrier + 6, ring: xs.ring - 6 };
  const headers = generalLabels
    ? { sun: ['Sun (S)', `Zs ${gear.zs}`], carrier: ['Carrier (C)', `${gear.n} planets`], ring: ['Ring (R)', `Zr ${gear.zr}`] }
    : AXIS_TEXT.ths;

  const els = [];
  // Zero-speed baseline
  els.push(h('line', { key: 'zero', x1: 20, x2: VW - 10, y1: n2(y(0)), y2: n2(y(0)), style: { stroke: C.muted, strokeWidth: 1, strokeDasharray: '4 3' } }));
  els.push(txt('zl', 20, y(0) - 4, '0 rpm', { size: 9.5, color: C.muted }));
  ['sun', 'carrier', 'ring'].forEach((m) => {
    const x = xs[m];
    els.push(h('line', { key: `ax-${m}`, x1: n2(x), x2: n2(x), y1: Y_TOP - 6, y2: Y_BOT + 6, style: { stroke: MEMBER_COLOR[m], strokeWidth: 2, strokeOpacity: 0.55 } }));
    els.push(txt(`h1-${m}`, labelX[m], 16, headers[m][0], { anchor: anchors[m], size: 11.5, weight: 700, color: MEMBER_COLOR[m] }));
    els.push(txt(`h2-${m}`, labelX[m], 31, headers[m][1], { anchor: anchors[m], size: 10.5, color: C.muted }));
  });
  // The lever: one straight line through the S and R points (the C point lies on it).
  els.push(h('line', {
    key: 'lever', x1: n2(xs.sun), y1: n2(y(rpm.sun)), x2: n2(xs.ring), y2: n2(y(rpm.ring)),
    'data-testid': 'ps-lever-line', style: { stroke: C.fg, strokeWidth: 2.5, strokeLinecap: 'round' },
  }));
  ['sun', 'carrier', 'ring'].forEach((m) => {
    const x = xs[m];
    const py = y(rpm[m]);
    // Torque arrow along the axis, starting at the lever point.
    const t = tq[m];
    if (Math.abs(t) > 1e-6) {
      const dir = t > 0 ? -1 : 1;
      // Keep the arrow between the headers and the torque labels.
      const room = dir < 0 ? py - (Y_TOP - 12) : (Y_BOT + 12) - py;
      const len = Math.max(12, Math.min(room, 10 + (MAX_ARROW - 10) * (Math.abs(t) / maxT)));
      const tipY = py + dir * len;
      const head = 7;
      els.push(h('g', { key: `tq-${m}`, 'data-testid': `ps-lever-torque-${m}`, 'data-dir': t > 0 ? 'up' : 'down' },
        h('line', { x1: n2(x), x2: n2(x), y1: n2(py), y2: n2(tipY - dir * head), style: { stroke: MEMBER_COLOR[m], strokeWidth: 3.2 } }),
        h('path', { d: `M${n2(x)},${n2(tipY)} L${n2(x - 5)},${n2(tipY - dir * head)} L${n2(x + 5)},${n2(tipY - dir * head)} Z`, style: { fill: MEMBER_COLOR[m] } })));
    }
    els.push(h('circle', {
      key: `pt-${m}`, cx: n2(x), cy: n2(py), r: 5.5, 'data-testid': `ps-lever-pt-${m}`, 'data-rpm': rpm[m],
      style: { fill: MEMBER_COLOR[m], stroke: C.card, strokeWidth: 1.5 },
    }));
    const valueText = `${fmtSigned(rpm[m], 0)} rpm`;
    // Lift a label off the dashed zero line when the point sits on it.
    const sideY = Math.abs(py - y(0)) < 7 ? py - 8 : py + 4;
    if (m === 'ring') els.push(txt(`v-${m}`, x + 9, sideY, valueText, { size: 10.5, weight: 700, halo: true }));
    else if (m === 'sun') els.push(txt(`v-${m}`, x - 9, sideY, valueText, { anchor: 'end', size: 10.5, weight: 700, halo: true }));
    else {
      // The lever passes through the carrier point from the left; put the label on the side
      // of the point away from it (below if the sun end is higher, above otherwise).
      const below = y(rpm.sun) < py;
      els.push(txt(`v-${m}`, x - 9, below ? py + 17 : py - 9, valueText, { anchor: 'end', size: 10.5, weight: 700, halo: true }));
    }
    els.push(txt(`t-${m}`, labelX[m], Y_BOT + 26, `T ${fmtSigned(t, 1)} N·m`, { anchor: anchors[m], size: 10, color: MEMBER_COLOR[m], weight: 600 }));
  });
  els.push(txt('sp', (xs.sun + xs.carrier) / 2, Y_BOT + 46, `S–C : C–R = Zr : Zs = ${gear.zr} : ${gear.zs}`, { anchor: 'middle', size: 9.5, color: C.muted }));

  return h('svg', {
    viewBox: `0 0 ${VW} ${VH}`, className: 'w-full h-auto max-w-[460px] mx-auto', role: 'img',
    'aria-label': 'Lever diagram of the planetary speeds', 'data-testid': 'ps-lever-svg',
  }, ...els);
};

export default LeverDiagram;
